import { useState } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { useTheme } from '../lib/ThemeContext';
import { useLanguage } from '../lib/LanguageContext';
import { useBuilding } from '../lib/BuildingContext';
import { Button } from '../components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '../components/ui/sheet';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import {
  LayoutDashboard, BookOpen, Award, User, Settings,
  Users, BarChart3, LogOut, Menu, ChevronRight, Boxes, ClipboardList, Trophy, Clapperboard, ShieldCheck, Image, FolderTree,
  Sun, Moon, Globe, ClipboardCheck, Grid3x3, FolderOpen, Eye, CalendarDays, Building2, Check, IdCard, Lightbulb, ScrollText, KeyRound, Lock, Sparkles
} from 'lucide-react';
import { isViewerRole } from '../lib/roles';
import NotificationBell from './NotificationBell';
import { DemoTour, startManagerTour } from './DemoTour';
import { BadgeCelebration } from './BadgeCelebration';
import { ShowcaseSwitcher, ShowcaseBanner } from './Showcase';

const LOGO_URL = "/yusen-logo.png";

const associateLinkKeys = [
  { to: '/dashboard', icon: LayoutDashboard, labelKey: 'nav.dashboard' },
  { to: '/assigned-tasks', icon: ClipboardList, labelKey: 'nav.assigned_tasks' },
  { to: '/training', icon: BookOpen, labelKey: 'nav.training' },
  { to: '/shorts', icon: Clapperboard, labelKey: 'nav.shorts' },
  { to: '/leaderboard', icon: Trophy, labelKey: 'nav.leaderboard' },
  { to: '/certificates', icon: Award, labelKey: 'nav.certificates' },
  { to: '/sops', icon: ScrollText, labelKey: 'nav.sops' },
  { to: '/profile', icon: User, labelKey: 'nav.profile' },
];

const adminLinkKeys = [
  { to: '/admin', icon: BarChart3, labelKey: 'nav.analytics' },
  { to: '/admin/modules', icon: Boxes, labelKey: 'nav.modules' },
  { to: '/shorts', icon: Clapperboard, labelKey: 'nav.shorts' },
  { to: '/admin/categories', icon: FolderTree, labelKey: 'nav.categories' },
  { to: '/admin/assignments', icon: ClipboardList, labelKey: 'nav.assignments' },
  { to: '/admin/associates', icon: Users, labelKey: 'nav.associates' },
  { to: '/admin/skill-matrix', icon: Grid3x3, labelKey: 'nav.skill_matrix' },
  { to: '/sops', icon: ScrollText, labelKey: 'nav.sops' },
  { to: '/admin/observations', icon: Eye, labelKey: 'nav.observations' },
  { to: '/training-signoff', icon: ClipboardCheck, labelKey: 'nav.training_signoff' },
  { to: '/admin/pit-licenses', icon: IdCard, labelKey: 'nav.pit_licenses' },
  { to: '/admin/kaizens', icon: Lightbulb, labelKey: 'nav.kaizens' },
  { to: '/admin/meetings', icon: CalendarDays, labelKey: 'nav.meetings' },
  { to: '/admin/buildings', icon: Building2, labelKey: 'nav.buildings' },
  { to: '/admin/media', icon: FolderOpen, labelKey: 'nav.media_library' },
  { to: '/admin/managers', icon: ShieldCheck, labelKey: 'nav.managers' },
  { to: '/admin/portal-accounts', icon: KeyRound, labelKey: 'nav.portal_accounts' },
  { to: '/admin/thumbnails', icon: Image, labelKey: 'nav.thumbnails' },
];

const viewerLinkKeys = [
  { to: '/portal', icon: BarChart3, labelKey: 'nav.analytics' },
  { to: '/portal/associates', icon: Users, labelKey: 'nav.associates' },
  { to: '/admin/skill-matrix', icon: Grid3x3, labelKey: 'nav.skill_matrix' },
  { to: '/sops', icon: ScrollText, labelKey: 'nav.sops' },
  { to: '/portal/profile', icon: User, labelKey: 'nav.profile' },
];

const managerLinkKeys = [
  { to: '/admin', icon: BarChart3, labelKey: 'nav.analytics' },
  { to: '/shorts', icon: Clapperboard, labelKey: 'nav.shorts' },
  { to: '/admin/assignments', icon: ClipboardList, labelKey: 'nav.assignments' },
  { to: '/admin/associates', icon: Users, labelKey: 'nav.associates' },
  { to: '/admin/skill-matrix', icon: Grid3x3, labelKey: 'nav.skill_matrix' },
  { to: '/sops', icon: ScrollText, labelKey: 'nav.sops' },
  { to: '/admin/observations', icon: Eye, labelKey: 'nav.observations' },
  { to: '/training-signoff', icon: ClipboardCheck, labelKey: 'nav.training_signoff' },
  { to: '/admin/pit-licenses', icon: IdCard, labelKey: 'nav.pit_licenses' },
  { to: '/admin/kaizens', icon: Lightbulb, labelKey: 'nav.kaizens' },
  { to: '/admin/meetings', icon: CalendarDays, labelKey: 'nav.meetings' },
];

function NavItem({ to, icon: Icon, label, onClick }) {
  return (
    <NavLink
      to={to}
      end={to === '/admin' || to === '/portal'}
      onClick={onClick}
      data-testid={`nav-${label.toLowerCase().replace(/\s/g, '-')}`}
      className={({ isActive }) =>
        `flex items-center gap-3 px-4 py-3 rounded-md text-sm font-medium transition-all duration-200 ${
          isActive
            ? 'bg-[#003366] text-white shadow-sm'
            : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
        }`
      }
    >
      <Icon size={18} />
      <span>{label}</span>
      <ChevronRight size={14} className="ml-auto opacity-40" />
    </NavLink>
  );
}

function SidebarContent({ onClose }) {
  const { user, logout, returnToAdmin } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const isAdmin = user?.role === 'admin';
  const isManager = user?.role === 'manager';
  const isTrainer = !!user?.is_trainer && user?.role === 'associate';
  const baseAssociateLinks = isTrainer
    ? [...associateLinkKeys, { to: '/training-signoff', icon: ClipboardCheck, labelKey: 'nav.training_signoff' }]
    : associateLinkKeys;
  const isViewer = isViewerRole(user);
  const linkKeys = isViewer ? viewerLinkKeys : isAdmin ? adminLinkKeys : isManager ? managerLinkKeys : baseAssociateLinks;

  const handleLogout = () => {
    if (user?.impersonated_by) { returnToAdmin(); window.location.assign('/admin'); return; }
    logout();
    navigate('/');
    if (onClose) onClose();
  };

  return (
    <div className="flex flex-col h-full">
      <div className="p-6 pb-4">
        <div className="flex items-center gap-3 mb-2">
          <img src={LOGO_URL} alt="Yusen Logistics" className="h-8 w-auto object-contain" />
        </div>
        <h2 className="text-lg font-bold text-[#003366] font-['Manrope']" style={{fontFamily: 'Manrope, sans-serif'}}>
          {t('nav.ind4_training')}
        </h2>
        <p className="text-xs text-slate-500 mt-0.5">{t('nav.dashboard_subtitle')}</p>
        {isViewer && (
          <div className="mt-3 rounded-md border border-[#F37021]/30 bg-[#F37021]/5 px-2.5 py-2" data-testid="viewer-scope-badge">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-[#D95D0F] flex items-center gap-1"><Lock size={11} /> {user.role === 'demo' ? 'Demo · sample data' : 'Customer · read-only'}</p>
            <p className="text-xs text-slate-600 truncate mt-0.5">{user.role === 'demo' ? 'Demo Sandbox' : `${user.customer_account_name || ''} · ${user.facility || ''}`}</p>
          </div>
        )}
      </div>
      <Separator />
      <ScrollArea className="flex-1 px-3 py-4">
        <div className="space-y-1">
          {linkKeys.map((link) => (
            <NavItem key={link.to} to={link.to} icon={link.icon} label={t(link.labelKey)} onClick={onClose} />
          ))}
        </div>
        {(isAdmin || isManager) && (
          <>
            <Separator className="my-4" />
            <p className="px-4 text-xs uppercase tracking-wider text-slate-400 font-medium mb-2">{t('nav.associate_view')}</p>
            <div className="space-y-1">
              {associateLinkKeys.filter(l => l.to !== '/sops' && l.to !== '/shorts').map((link) => (
                <NavItem key={link.to} to={link.to} icon={link.icon} label={t(link.labelKey)} onClick={onClose} />
              ))}
            </div>
          </>
        )}
      </ScrollArea>
      <Separator />
      <div className="p-4">
        <div className="flex items-center gap-3 mb-3 px-2">
          {user?.profile_picture ? (
            <img src={user.profile_picture} alt={user.name} className="w-9 h-9 rounded-full object-cover flex-shrink-0" />
          ) : (
            <div className="w-9 h-9 rounded-full bg-[#003366] flex items-center justify-center text-white text-sm font-semibold flex-shrink-0">
              {user?.name?.charAt(0)?.toUpperCase() || 'U'}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-slate-900 truncate">{user?.name}</p>
            <p className="text-xs text-slate-500 truncate">{isViewer ? (user.company || (user.role === 'demo' ? 'Demo account' : 'Customer account')) : (user?.job_title || user?.role)}</p>
          </div>
        </div>
        {isManager && (
          <Button variant="ghost" className="w-full justify-start text-[#F37021] hover:text-[#D95D0F] hover:bg-orange-50 mb-1" onClick={() => { onClose?.(); startManagerTour(); }} data-testid="manager-tour-btn">
            <Sparkles size={16} className="mr-2" /> Take the tour
          </Button>
        )}
        <Button
          variant="ghost"
          className="w-full justify-start text-slate-500 hover:text-red-600 hover:bg-red-50"
          onClick={handleLogout}
          data-testid="logout-btn"
        >
          <LogOut size={16} className="mr-2" />
          {t('nav.sign_out')}
        </Button>
      </div>
    </div>
  );
}

export default function Layout({ children }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [buildingMenu, setBuildingMenu] = useState(false);
  const location = useLocation();
  const { theme, toggleTheme } = useTheme();
  const { lang, setLang, t } = useLanguage();
  const { user } = useAuth();
  const { buildings, activeBuilding, setActiveBuilding } = useBuilding();
  const isAdmin = user?.role === 'admin';

  const activeBuildingLabel = activeBuilding === 'all'
    ? 'All Buildings'
    : (buildings.find(b => b.name === activeBuilding)?.name || activeBuilding);

  const pageTitle = (() => {
    const path = location.pathname;
    if (path === '/dashboard') return t('page.dashboard');
    if (path === '/assigned-tasks') return t('page.assigned_tasks');
    if (path === '/training') return t('page.training_modules');
    if (path.startsWith('/module/')) return t('page.module_viewer');
    if (path === '/certificates') return t('page.my_certificates');
    if (path.startsWith('/certificate/')) return t('page.certificate');
    if (path === '/profile') return t('page.my_profile');
    if (path === '/shorts') return t('page.shorts');
    if (path === '/leaderboard') return t('page.leaderboard');
    if (path === '/admin/managers') return t('page.manage_managers');
    if (path === '/admin/thumbnails') return t('page.category_thumbnails');
    if (path === '/admin/categories') return t('page.manage_categories');
    if (path === '/admin') return t('page.admin_analytics');
    if (path === '/admin/modules') return t('page.manage_modules');
    if (path.startsWith('/admin/modules/')) return 'Lesson Builder';
    if (path === '/admin/assignments') return t('page.manage_assignments');
    if (path === '/admin/associates') return t('page.manage_associates');
    if (path === '/admin/reviews') return t('page.open_answer_reviews');
    if (path === '/admin/skill-matrix') return t('page.skill_matrix');
    if (path === '/portal') return t('page.portal_analytics');
    if (path === '/portal/associates') return t('page.portal_associates');
    if (path === '/portal/profile') return t('page.my_profile');
    if (path === '/admin/portal-accounts') return t('page.portal_accounts');
    if (path === '/sops') return t('page.sops');
    if (path === '/admin/media') return t('page.media_library');
    const extra = { '/admin/observations': 'Observations', '/training-signoff': 'Training Sign-Off', '/admin/meetings': 'Meetings', '/admin/buildings': 'Buildings', '/admin/pit-licenses': 'PIT Licenses', '/admin/kaizens': 'Kaizens' };
    if (extra[path]) return extra[path];
    return t('page.ind4_training');
  })();

  return (
    <div className="flex h-screen bg-[#F8FAFC]">
      <aside className="hidden lg:flex w-[260px] border-r border-slate-200 flex-shrink-0">
        <SidebarContent />
      </aside>
      <div className="flex-1 flex flex-col min-w-0">
        <ShowcaseBanner />
        <header className="h-16 border-b border-slate-200 bg-white flex items-center px-6 gap-4 flex-shrink-0">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild className="lg:hidden">
              <Button variant="ghost" size="icon" data-testid="mobile-menu-btn">
                <Menu size={20} />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="p-0 w-[260px]">
              <SidebarContent onClose={() => setMobileOpen(false)} />
            </SheetContent>
          </Sheet>
          <h1 className="text-base sm:text-xl font-bold text-[#003366] flex-1 min-w-0 truncate" style={{fontFamily: 'Manrope, sans-serif'}}>
            <span className="hidden sm:inline">{pageTitle}</span>
          </h1>
          <div className="flex items-center gap-1">
            {isAdmin && buildings.length > 0 && (
              <div className="relative" data-testid="building-switcher">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setBuildingMenu(v => !v)}
                  className="flex items-center gap-1.5 text-slate-600 hover:text-[#003366] px-2.5 border border-slate-200 rounded-md"
                  data-testid="building-switcher-btn"
                >
                  <Building2 size={14} className="text-[#003366]" />
                  <span className="text-xs font-semibold hidden sm:inline">{activeBuildingLabel}</span>
                </Button>
                {buildingMenu && (
                  <>
                    <div className="fixed inset-0 z-30" onClick={() => setBuildingMenu(false)} />
                    <div className="absolute right-0 top-full mt-1 z-40 min-w-[180px] rounded-md border border-slate-200 bg-white shadow-lg py-1" data-testid="building-switcher-menu">
                      <button
                        type="button"
                        onClick={() => { setActiveBuilding('all'); setBuildingMenu(false); }}
                        className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between hover:bg-slate-50 ${activeBuilding === 'all' ? 'text-[#003366] font-medium' : 'text-slate-600'}`}
                        data-testid="building-option-all"
                      >
                        <span>All Buildings</span>
                        {activeBuilding === 'all' && <Check size={14} className="text-[#003366]" />}
                      </button>
                      <div className="my-1 border-t border-slate-100" />
                      {buildings.map(b => (
                        <button
                          key={b.id}
                          type="button"
                          onClick={() => { setActiveBuilding(b.name); setBuildingMenu(false); }}
                          className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between hover:bg-slate-50 ${activeBuilding === b.name ? 'text-[#003366] font-medium' : 'text-slate-600'}`}
                          data-testid={`building-option-${b.name}`}
                        >
                          <span>{b.name}</span>
                          {activeBuilding === b.name && <Check size={14} className="text-[#003366]" />}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}
            {user?.role === 'admin' && !user.impersonated_by && <ShowcaseSwitcher />}
            {!isViewerRole(user) && !user?.impersonated_by && <NotificationBell />}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setLang(lang === 'en' ? 'es' : 'en')}
              className="flex items-center gap-1.5 text-slate-500 hover:text-[#003366] px-2.5"
              data-testid="lang-toggle-btn"
            >
              <Globe size={16} />
              <span className="text-xs font-semibold uppercase">{lang === 'en' ? 'ES' : 'EN'}</span>
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              className="text-slate-500 hover:text-[#003366]"
              data-testid="theme-toggle-btn"
            >
              {theme === 'light' ? <Moon size={18} /> : <Sun size={18} />}
            </Button>
          </div>
        </header>
        <DemoTour enabled={user?.role === 'demo'} />
        <DemoTour enabled={user?.role === 'manager'} tour="manager" autoStart={!!user?.is_demo} userId={user?.id} />
        <BadgeCelebration enabled={!!user && !isViewerRole(user) && !user.must_change_password} />
        <main className="flex-1 overflow-auto">
          {location.pathname === '/shorts' ? children : (
            <div key={location.pathname} className="page-enter p-6 md:p-8 lg:p-12 max-w-[1400px] mx-auto">
              {children}
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
